import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, ChevronDown, ChevronUp, UserPlus, CreditCard, 
  Shield, Calendar, Star, MessageCircle, FileText, Euro,
  CheckCircle, ArrowRight, Compass, Users, Camera, Clock,
  Briefcase, BadgeCheck, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const GuideFaqPage = () => {
  const { i18n } = useTranslation();
  const [openSection, setOpenSection] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqSections = [
    {
      id: 'getting-started',
      icon: UserPlus,
      title: i18n.language === 'fr' ? 'Devenir Guide' : 'Becoming a Guide',
      color: 'bg-blue-100 text-blue-600',
      questions: [
        {
          q: i18n.language === 'fr' ? 'Comment puis-je m\'inscrire comme guide ?' : 'How do I register as a guide?',
          a: i18n.language === 'fr' 
            ? 'Cliquez sur "Inscription" et sélectionnez "Devenir Explorateur". Complétez votre profil avec une photo, une bio, vos spécialités et les langues que vous parlez. Acceptez ensuite la Charte des Guides pour commencer à créer vos expériences.'
            : 'Click "Sign Up" and select "Become an Explorer". Complete your profile with a photo, bio, specialties, and languages you speak. Then accept the Guide Charter to start creating your experiences.'
        },
        {
          q: i18n.language === 'fr' ? 'Quelles sont les deux options pour les guides ?' : 'What are the two guide options?',
          a: i18n.language === 'fr'
            ? '<strong>1. Contre rétribution :</strong> Vous proposez des expériences payantes et recevez le paiement directement. XPerience prélève une commission de 15% sur chaque réservation. Inscription gratuite.<br/><br/><strong>2. Par passion :</strong> Vous proposez des expériences gratuites pour partager votre passion. Inscription gratuite. Les voyageurs souhaitant réserver vos expériences gratuites doivent souscrire un abonnement annuel de 49€.'
            : '<strong>1. For compensation:</strong> You offer paid experiences and receive payment directly. XPerience takes a 15% commission on each booking. Free registration.<br/><br/><strong>2. For passion:</strong> You offer free experiences to share your passion. Free registration. Travelers who want to book your free experiences must subscribe annually for €49.'
        },
        {
          q: i18n.language === 'fr' ? 'Quels documents sont nécessaires ?' : 'What documents are needed?',
          a: i18n.language === 'fr'
            ? 'Aucun document n\'est obligatoire pour commencer. Cependant, pour obtenir le badge "Guide Vérifié", vous devrez fournir une pièce d\'identité et éventuellement des certifications professionnelles (guide touristique, moniteur sportif, etc.).'
            : 'No documents are required to get started. However, to obtain the "Verified Guide" badge, you will need to provide an ID and possibly professional certifications (tour guide, sports instructor, etc.).'
        }
      ]
    },
    {
      id: 'experiences',
      icon: Compass,
      title: i18n.language === 'fr' ? 'Créer des Expériences' : 'Creating Experiences',
      color: 'bg-orange-100 text-orange-600',
      questions: [
        {
          q: i18n.language === 'fr' ? 'Comment créer une expérience ?' : 'How do I create an experience?',
          a: i18n.language === 'fr'
            ? 'Depuis votre tableau de bord, cliquez sur "Créer une expérience". Remplissez le titre, la description, les catégories, le prix, la durée, le nombre max de participants, et ajoutez de belles photos. La politique d\'annulation XPerience est appliquée automatiquement.'
            : 'From your dashboard, click "Create an experience". Fill in the title, description, categories, price, duration, max participants, and add beautiful photos. The XPerience cancellation policy is applied automatically.'
        },
        {
          q: i18n.language === 'fr' ? 'Combien d\'expériences puis-je créer ?' : 'How many experiences can I create?',
          a: i18n.language === 'fr'
            ? 'Il n\'y a pas de limite ! Vous pouvez créer autant d\'expériences que vous le souhaitez. Diversifiez vos offres pour attirer différents types de voyageurs.'
            : 'There\'s no limit! You can create as many experiences as you want. Diversify your offerings to attract different types of travelers.'
        },
        {
          q: i18n.language === 'fr' ? 'Comment fixer mes prix ?' : 'How do I set my prices?',
          a: i18n.language === 'fr'
            ? 'Vous êtes libre de fixer vos prix. Pensez à inclure vos coûts (transport, matériel, assurance) et votre temps. Consultez les expériences similaires pour vous positionner. N\'oubliez pas que XPerience prélève 15% de commission sur les expériences payantes.'
            : 'You\'re free to set your prices. Consider including your costs (transport, equipment, insurance) and your time. Check similar experiences to position yourself. Don\'t forget that XPerience takes a 15% commission on paid experiences.'
        },
        {
          q: i18n.language === 'fr' ? 'Puis-je proposer des services additionnels ?' : 'Can I offer additional services?',
          a: i18n.language === 'fr'
            ? 'Oui ! Dans votre tableau de bord, onglet "Mes services", vous pouvez ajouter des services comme : véhicule, hébergement, équipement, repas ou transfert. Ces services seront affichés sur votre profil et aideront les voyageurs à vous trouver via les filtres de recherche.'
            : 'Yes! In your dashboard, "My services" tab, you can add services like: vehicle, accommodation, equipment, meals, or transfer. These services will be displayed on your profile and help travelers find you via search filters.'
        }
      ]
    },
    {
      id: 'payments',
      icon: Euro,
      title: i18n.language === 'fr' ? 'Paiements & Revenus' : 'Payments & Earnings',
      color: 'bg-green-100 text-green-600',
      questions: [
        {
          q: i18n.language === 'fr' ? 'Comment recevoir mes paiements ?' : 'How do I receive my payments?',
          a: i18n.language === 'fr'
            ? 'Connectez votre compte Stripe depuis votre tableau de bord (carte "Paiements Stripe"). Stripe gère les paiements de manière sécurisée et vous verse directement sur votre compte bancaire après chaque expérience réalisée.'
            : 'Connect your Stripe account from your dashboard ("Stripe Payments" card). Stripe securely handles payments and transfers directly to your bank account after each completed experience.'
        },
        {
          q: i18n.language === 'fr' ? 'Quelle est la commission de XPerience ?' : 'What is XPerience\'s commission?',
          a: i18n.language === 'fr'
            ? '<strong>Pour les expériences payantes :</strong> 15% de commission sur chaque réservation.<br/><br/><strong>Pour les expériences gratuites :</strong> Aucune commission. Les guides "passion" proposent gratuitement leurs expériences. Ce sont les voyageurs qui paient un abonnement annuel de 49€ pour y accéder.'
            : '<strong>For paid experiences:</strong> 15% commission on each booking.<br/><br/><strong>For free experiences:</strong> No commission. "Passion" guides offer their experiences for free. Travelers pay an annual subscription of €49 to access them.'
        },
        {
          q: i18n.language === 'fr' ? 'Quand suis-je payé ?' : 'When do I get paid?',
          a: i18n.language === 'fr'
            ? 'Le paiement est versé sur votre compte Stripe 24-48h après la réalisation de l\'expérience. Les délais de virement vers votre banque dépendent de Stripe (généralement 2-7 jours ouvrés).'
            : 'Payment is sent to your Stripe account 24-48h after the experience is completed. Transfer times to your bank depend on Stripe (usually 2-7 business days).'
        },
        {
          q: i18n.language === 'fr' ? 'Que se passe-t-il en cas d\'annulation ?' : 'What happens in case of cancellation?',
          a: i18n.language === 'fr'
            ? 'XPerience applique une politique d\'annulation uniforme pour toutes les expériences :<br/><br/><strong>Plus de 24h avant :</strong> Remboursement 100% pour le voyageur.<br/><strong>Moins de 24h avant :</strong> Pas de remboursement.<br/><strong>Météo / Force majeure :</strong> Remboursement 100% ou report.<br/><br/>Cette politique protège à la fois les guides et les voyageurs.'
            : 'XPerience applies a uniform cancellation policy for all experiences:<br/><br/><strong>More than 24h before:</strong> 100% refund for the traveler.<br/><strong>Less than 24h before:</strong> No refund.<br/><strong>Weather / Force majeure:</strong> 100% refund or reschedule.<br/><br/>This policy protects both guides and travelers.'
        }
      ]
    },
    {
      id: 'verification',
      icon: BadgeCheck,
      title: i18n.language === 'fr' ? 'Vérification & Confiance' : 'Verification & Trust',
      color: 'bg-purple-100 text-purple-600',
      questions: [
        {
          q: i18n.language === 'fr' ? 'Comment obtenir le badge "Guide Vérifié" ?' : 'How do I get the "Verified Guide" badge?',
          a: i18n.language === 'fr'
            ? 'Depuis votre tableau de bord, cliquez sur "Demander la vérification". Fournissez une pièce d\'identité et toute certification pertinente. Notre équipe examinera votre demande sous 48-72h. Le badge vérifié augmente la confiance des voyageurs et améliore votre visibilité.'
            : 'From your dashboard, click "Request verification". Provide an ID and any relevant certifications. Our team will review your request within 48-72h. The verified badge increases traveler trust and improves your visibility.'
        },
        {
          q: i18n.language === 'fr' ? 'Comment fonctionne le système d\'avis ?' : 'How does the review system work?',
          a: i18n.language === 'fr'
            ? 'Après chaque expérience, les voyageurs peuvent laisser un avis et une note (1-5 étoiles). Votre note moyenne et le nombre d\'avis sont affichés sur votre profil. Répondez toujours aux avis pour montrer votre engagement !'
            : 'After each experience, travelers can leave a review and rating (1-5 stars). Your average rating and number of reviews are displayed on your profile. Always respond to reviews to show your engagement!'
        },
        {
          q: i18n.language === 'fr' ? 'Qu\'est-ce que la Charte des Guides ?' : 'What is the Guide Charter?',
          a: i18n.language === 'fr'
            ? 'La Charte des Guides est un engagement de qualité et de respect. En l\'acceptant, vous vous engagez à offrir des expériences authentiques, à respecter les horaires, à communiquer clairement avec les voyageurs, et à respecter les normes de sécurité.'
            : 'The Guide Charter is a commitment to quality and respect. By accepting it, you commit to offering authentic experiences, respecting schedules, communicating clearly with travelers, and following safety standards.'
        }
      ]
    },
    {
      id: 'bookings',
      icon: Calendar,
      title: i18n.language === 'fr' ? 'Réservations & Organisation' : 'Bookings & Organization',
      color: 'bg-yellow-100 text-yellow-600',
      questions: [
        {
          q: i18n.language === 'fr' ? 'Comment gérer mes réservations ?' : 'How do I manage my bookings?',
          a: i18n.language === 'fr'
            ? 'Toutes vos réservations sont accessibles dans votre tableau de bord, onglet "Mes réservations". Vous y trouverez les détails (date, heure, participants), pouvez contacter les voyageurs, et confirmer ou annuler les réservations.'
            : 'All your bookings are accessible in your dashboard, "My bookings" tab. You\'ll find the details (date, time, participants), can contact travelers, and confirm or cancel bookings.'
        },
        {
          q: i18n.language === 'fr' ? 'Comment communiquer avec les voyageurs ?' : 'How do I communicate with travelers?',
          a: i18n.language === 'fr'
            ? 'Utilisez la messagerie intégrée pour échanger avec les voyageurs avant, pendant et après l\'expérience. Vous recevez aussi des notifications par email pour ne rien manquer.'
            : 'Use the built-in messaging to communicate with travelers before, during, and after the experience. You\'ll also receive email notifications so you don\'t miss anything.'
        },
        {
          q: i18n.language === 'fr' ? 'Puis-je définir mes disponibilités ?' : 'Can I set my availability?',
          a: i18n.language === 'fr'
            ? 'Oui ! Dans votre tableau de bord, vous pouvez vous rendre disponible ou indisponible. Quand vous êtes disponible, vous apparaissez sur la carte et les voyageurs peuvent vous contacter directement.'
            : 'Yes! In your dashboard, you can set yourself as available or unavailable. When you\'re available, you appear on the map and travelers can contact you directly.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
    setOpenQuestion(null);
  };

  const toggleQuestion = (questionIndex) => {
    setOpenQuestion(openQuestion === questionIndex ? null : questionIndex);
  };

  return (
    <main className="min-h-screen pt-20 pb-16 bg-gradient-to-b from-orange-50/50 to-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center py-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center"
          >
            <HelpCircle className="w-10 h-10 text-primary" />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Guide des Explorateurs' : 'Explorer Guide'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {i18n.language === 'fr' 
              ? 'Tout ce que vous devez savoir pour devenir guide sur XPerience et proposer des expériences inoubliables.'
              : 'Everything you need to know to become a guide on XPerience and offer unforgettable experiences.'}
          </p>
        </div>

        {/* Quick Start CTA */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-primary to-orange-500 rounded-2xl p-6 mb-12 text-white"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold mb-2">
                {i18n.language === 'fr' ? 'Prêt à commencer ?' : 'Ready to start?'}
              </h2>
              <p className="text-white/90">
                {i18n.language === 'fr' 
                  ? 'Créez votre compte gratuitement et commencez à partager vos expériences.'
                  : 'Create your account for free and start sharing your experiences.'}
              </p>
            </div>
            <Link to="/register">
              <Button variant="secondary" size="lg" className="gap-2 whitespace-nowrap">
                {i18n.language === 'fr' ? 'Devenir Guide' : 'Become a Guide'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* FAQ Sections */}
        <div className="space-y-4">
          {faqSections.map((section, sectionIndex) => {
            const Icon = section.icon;
            const isOpen = openSection === section.id;
            
            return (
              <motion.div
                key={section.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 + sectionIndex * 0.05 }}
                className="bg-white rounded-2xl border shadow-sm overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-5 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                  data-testid={`faq-section-${section.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${section.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="flex-1 text-left font-semibold text-lg">{section.title}</span>
                  <span className="text-sm text-muted-foreground mr-2">
                    {section.questions.length} {i18n.language === 'fr' ? 'questions' : 'questions'}
                  </span>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {/* Questions */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t"
                    >
                      <div className="p-4 space-y-2">
                        {section.questions.map((item, qIndex) => {
                          const questionKey = `${section.id}-${qIndex}`;
                          const isQuestionOpen = openQuestion === questionKey;
                          
                          return (
                            <div key={qIndex} className="border rounded-xl overflow-hidden">
                              <button
                                onClick={() => toggleQuestion(questionKey)}
                                className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                              >
                                <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isQuestionOpen ? 'text-primary' : 'text-gray-300'}`} />
                                <span className="flex-1 font-medium">{item.q}</span>
                                {isQuestionOpen ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                              
                              <AnimatePresence>
                                {isQuestionOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t bg-gray-50"
                                  >
                                    <div 
                                      className="p-4 text-gray-600 leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: item.a }}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Contact Support */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground mb-4">
            {i18n.language === 'fr' 
              ? 'Vous ne trouvez pas la réponse à votre question ?'
              : 'Can\'t find the answer to your question?'}
          </p>
          <Link to="/contact">
            <Button variant="outline" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              {i18n.language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
            </Button>
          </Link>
        </motion.div>

        {/* Key Benefits */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { icon: Users, label: i18n.language === 'fr' ? 'Voyageurs du monde' : 'Global Travelers', value: '10k+' },
            { icon: Compass, label: i18n.language === 'fr' ? 'Expériences' : 'Experiences', value: '500+' },
            { icon: Star, label: i18n.language === 'fr' ? 'Note moyenne' : 'Average Rating', value: '4.8' },
            { icon: Euro, label: i18n.language === 'fr' ? 'Revenus générés' : 'Revenue Generated', value: '250k€' }
          ].map((stat, index) => (
            <div key={index} className="text-center p-4 bg-white rounded-xl border">
              <stat.icon className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </main>
  );
};

export default GuideFaqPage;
